/**
 * LoginForm Component
 * Form for user authentication with test credentials display
 * Uses only Ant Design components
 */

import React from "react";

import {
  MailOutlined,
  LockOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Input,
  Alert,
  Checkbox,
  Form,
  Typography,
  Space,
} from "antd";
import { Link } from "react-router-dom";

import { AUTH_ROUTES } from "../routes";
import { TEST_CREDENTIALS } from "../store/authStore";

import type { LoginFormData } from "../validations/loginSchema";

const { Text, Paragraph } = Typography;

interface LoginFormProps {
  onSubmit: (data: LoginFormData) => Promise<void>;
  error?: string | null;
  isLoading?: boolean;
}

export const LoginForm: React.FC<LoginFormProps> = ({
  onSubmit,
  error,
  isLoading,
}) => {
  const [form] = Form.useForm();

  const handleFillDemo = () => {
    form.setFieldsValue({
      email: TEST_CREDENTIALS.email,
      password: TEST_CREDENTIALS.password,
    });
  };

  const handleFinish = async (values: LoginFormData) => {
    await onSubmit(values);
  };

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          closable
          style={{ marginBottom: "8px" }}
        />
      )}

      {/* Demo Credentials Card */}
      <Card
        style={{
          background: "rgba(187, 136, 0, 0.08)",
          border: "1px solid rgba(187, 136, 0, 0.2)",
          borderRadius: "8px",
        }}
        styles={{ body: { padding: "16px" } }}
      >
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          <Space size="small">
            <InfoCircleOutlined style={{ color: "#BB8800" }} />
            <Text strong style={{ color: "#fff" }}>
              Demo Credentials
            </Text>
          </Space>
          <Space direction="vertical" size={4} style={{ width: "100%" }}>
            <Paragraph style={{ margin: 0 }}>
              <Text type="secondary" style={{ fontSize: "13px" }}>
                Email:
              </Text>{" "}
              <Text
                code
                style={{
                  fontSize: "13px",
                  background: "rgba(187, 136, 0, 0.15)",
                }}
              >
                {TEST_CREDENTIALS.email}
              </Text>
            </Paragraph>
            <Paragraph style={{ margin: 0 }}>
              <Text type="secondary" style={{ fontSize: "13px" }}>
                Password:
              </Text>{" "}
              <Text
                code
                style={{
                  fontSize: "13px",
                  background: "rgba(187, 136, 0, 0.15)",
                }}
              >
                {TEST_CREDENTIALS.password}
              </Text>
            </Paragraph>
          </Space>
          <Button
            type="link"
            size="small"
            onClick={handleFillDemo}
            style={{
              padding: 0,
              height: "auto",
              color: "#BB8800",
              fontSize: "13px",
            }}
          >
            Fill Demo Credentials
          </Button>
        </Space>
      </Card>

      <Form
        form={form}
        onFinish={handleFinish}
        layout="vertical"
        size="large"
        style={{ marginTop: "8px" }}
        initialValues={{
          email: "",
          password: "",
          rememberMe: false,
        }}
      >
        <Form.Item
          name="email"
          label={
            <Text style={{ color: "rgba(224, 224, 224, 0.88)" }}>Email</Text>
          }
          rules={[
            { required: true, message: "Email is required" },
            { type: "email", message: "Please enter a valid email" },
          ]}
          style={{ marginBottom: "20px" }}
        >
          <Input
            type="email"
            placeholder="Enter your email"
            prefix={
              <MailOutlined style={{ color: "rgba(224, 224, 224, 0.45)" }} />
            }
            style={{ height: "44px" }}
          />
        </Form.Item>

        <Form.Item
          name="password"
          label={
            <Text style={{ color: "rgba(224, 224, 224, 0.88)" }}>Password</Text>
          }
          rules={[{ required: true, message: "Password is required" }]}
          style={{ marginBottom: "20px" }}
        >
          <Input.Password
            placeholder="Enter your password"
            prefix={
              <LockOutlined style={{ color: "rgba(224, 224, 224, 0.45)" }} />
            }
            style={{ height: "44px" }}
          />
        </Form.Item>

        <Form.Item
          name="rememberMe"
          valuePropName="checked"
          style={{ marginBottom: "24px" }}
        >
          <Checkbox style={{ color: "rgba(224, 224, 224, 0.88)" }}>
            Remember me
          </Checkbox>
        </Form.Item>

        <Form.Item style={{ marginBottom: "16px" }}>
          <Button
            type="primary"
            htmlType="submit"
            loading={isLoading}
            block
            style={{
              height: "44px",
              fontSize: "16px",
              fontWeight: 500,
            }}
          >
            Sign in
          </Button>
        </Form.Item>
      </Form>

      <Text
        type="secondary"
        style={{
          textAlign: "center",
          display: "block",
          fontSize: "14px",
          marginTop: "8px",
        }}
      >
        Don&apos;t have an account?{" "}
        <Link to={AUTH_ROUTES.REGISTER}>
          <Text style={{ color: "#BB8800" }}>Sign up</Text>
        </Link>
      </Text>
    </Space>
  );
};

export default LoginForm;
